import { useState } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { BUTTON_UPLOAD_CONTENT } from "../../i18n/keysTranslations";
import {
  IMAGE_FORMATS,
  VIDEO_FORMATS,
  FONT_SIZE_BUTTON,
  META,
  TIKTOK,
} from "../../utils/constants";
import { useDispatchApp } from "../../lib/redux";
import { useTranslationApp } from "../../lib/i18next";
import Loader from "../Loaders/Loader";
import Button from "./Button";
import ModalGallery from "../Modal/ModalGallery";
import ModalGalleryAdsPlatforms from "../Modal/ModalGalleryAdsPlatforms";
import ModalReuseCreativesList from "../Modal/ModalReuseCreativesList";
import UploadFileButton from "./UploadFileButton";
import { startAddContentsToList } from "../../actions/user";
import { usePlatformConfigurations } from "../../hooks/usePlatformConfigurations";

const IconButtonAddContent = ({
  onAddContent: onAddContentCallback = () => {},
  pathStorage,
  pathRTDB,
  modeUpload = "sync",
  mode = "icon",
  multiple = false,
  textButton = BUTTON_UPLOAD_CONTENT,
  acceptedFormats = IMAGE_FORMATS.concat(VIDEO_FORMATS),
  sx = {},
  sxButton = {},
  showButtonUpload = true,
  urlContent = "",
  size = "medium",
  color = "secondary",
  showUploadDeviceOption = true,
  showGalleryOption = true,
  id = "add-content-media",
  minDimensions = {},
  disabled = false,
  forcePostSelection = false,
  forceReuseSelection = false,
  propsReuseCreatives = {},
  propsPostsAdsPlatforms = {},
  startIconButton = undefined,
  initialValues = {},
  icon = (
    <AddCircleIcon sx={{ color: "#000 !important", height: 40, width: 40 }} />
  ),
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [modalGalleryOpen, setModalGalleryOpen] = useState(false);
  const [modalReuseOpen, setModalReuseOpen] = useState(false);

  const dispatch = useDispatchApp();
  const { t } = useTranslationApp();

  const platformConfigurations = propsPostsAdsPlatforms;
  const {
    meta: {
      integrationID: metaIntegrationID,
      adAccountID: metaAdAccountID,
      pageID: metaPageID,
    },
  } = usePlatformConfigurations(platformConfigurations);

  // Determine if carousel mode should be enabled based on platform
  const platform = initialValues?.platform;
  const isCarouselMode = platform === META || platform === TIKTOK;

  const onOpenPicker = () => {
    if (forceReuseSelection) {
      setModalReuseOpen(true);
      return;
    }
    setModalGalleryOpen(true);
  };

  const onAddContent = async (newMedias) => {
    let contents = [];
    for (const media of newMedias) {
      contents.push({
        url: media,
        type: "upload",
      });
    }

    if (modeUpload === "sync") {
      dispatch(startAddContentsToList(contents));
    }

    await onAddContentCallback(contents);
  };

  const onAddContentFromCollaborations = async (contents) => {
    setIsLoading(true);
    await onAddContentCallback(contents);
    setIsLoading(false);
  };

  const onSelectReuseCreative = async (creative) => {
    setIsLoading(true);
    await onAddContentCallback([
      {
        ...creative,
        type: "reuseCreative",
        creativeID: creative.creativeID,
        url: creative.mediaUrls?.[0] || "",
        mediaUrls: creative.mediaUrls || [],
        isCarousel: Boolean(creative.isCarousel),
        contentType: creative.contentType,
        from: "reuseCreative",
      },
    ]);
    setIsLoading(false);
  };

  const getFilterModalGallery = () => {
    // When carousel mode is active, don't filter by format to allow carousels to be shown
    // Carousels can contain images even when only videos are accepted
    if (isCarouselMode) {
      return null;
    }

    let hasVideoFormat = "";
    let hasImageFormat = "";

    acceptedFormats.forEach((format) => {
      if (VIDEO_FORMATS.includes(format)) {
        hasVideoFormat = true;
      }
      if (IMAGE_FORMATS.includes(format)) {
        hasImageFormat = true;
      }
    });

    if (hasVideoFormat && hasImageFormat) {
      return null;
    }

    if (hasVideoFormat) {
      return "video";
    }

    if (hasImageFormat) {
      return "image";
    }
  };

  const filterModalGallery = getFilterModalGallery();

  return (
    <Box
      sx={{
        height: mode === "icon" ? 92 : "auto",
        width: mode === "icon" ? 92 : "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        ...sx,
      }}
    >
      <UploadFileButton
        id={id}
        value={urlContent}
        onChange={onAddContent}
        acceptedFormats={acceptedFormats}
        multiple={multiple}
        minDimensions={minDimensions}
        pathStorage={pathStorage}
        pathRTDB={pathRTDB}
        modeUpload={modeUpload}
        onCallbackLoading={(loading) => {
          if (modeUpload === "sync") {
            setIsLoading(loading);
          }
        }}
        isHidden={true}
      />
      {isLoading && (
        <Loader colorCustom="black" hasMarginTop={false} fullWidth={true} />
      )}
      {!isLoading && showButtonUpload && (
        <>
          {mode === "icon" ? (
            <IconButton
              disabled={disabled}
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenPicker();
              }}
              type="button"
            >
              {icon}
            </IconButton>
          ) : (
            <Button
              disabled={disabled}
              size={size}
              variant="contained"
              color={color}
              onClick={onOpenPicker}
              type="button"
              startIcon={startIconButton}
              sx={{
                fontSize: FONT_SIZE_BUTTON,
                width: "100%",
                ...sxButton,
              }}
            >
              {t(textButton)}
            </Button>
          )}
        </>
      )}
      {modalGalleryOpen && forcePostSelection && (
        <ModalGalleryAdsPlatforms
          modalOpen={modalGalleryOpen}
          onCloseModal={() => setModalGalleryOpen(false)}
          onCallbackSelectedItems={onAddContentFromCollaborations}
          pathStorage={pathStorage}
          multiple={multiple}
          compact={true}
          forceSocialPostsSource={true}
          integrationID={metaIntegrationID}
          adAccountID={metaAdAccountID}
          pageID={metaPageID}
        />
      )}
      {modalGalleryOpen && showGalleryOption && !forcePostSelection && (
        <ModalGallery
          filterByFormat={filterModalGallery}
          acceptedFormats={acceptedFormats}
          multiple={multiple}
          modalOpen={modalGalleryOpen}
          onCloseModal={() => setModalGalleryOpen(false)}
          onCallbackSelectedItems={onAddContentFromCollaborations}
          externalUploadInputId={id}
          pathStorage={pathStorage}
          pathRTDB={pathRTDB}
          modeUpload={modeUpload}
          minDimensions={minDimensions}
          showUploadDeviceOption={showUploadDeviceOption}
          showAdAccountOption={true}
          showSocialPostsOption={true}
          platformConfigurations={platformConfigurations}
          initialValues={initialValues}
          isCarouselMode={isCarouselMode}
        />
      )}
      {modalReuseOpen && forceReuseSelection && (
        <ModalReuseCreativesList
          modalOpen={modalReuseOpen}
          onCloseModal={() => setModalReuseOpen(false)}
          onCallbackSelect={onSelectReuseCreative}
          {...propsReuseCreatives}
        />
      )}
    </Box>
  );
};

export default IconButtonAddContent;
